// Signing in — no password. docs/CLIENT_PORTAL.md §5.
//
// Two ways in, from one email:
//   · the LINK, /portal/sign-in#t=<token>. The token is in the #fragment, which
//     a browser never sends, so a mail scanner that fetches the link consumes
//     nothing. This page lifts it out of the address bar at once (so it is not
//     left in history or on a shared screen) and uses it only when the person
//     presses Continue.
//   · the CODE, six digits, typed here — only in the browser that asked for it
//     (the server binds it to a cookie this page's request set).
//
// "Email me a link" answers the same thing for every address, so this page
// never says whether an address has an account.

import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FwButton, FwField, FwInput, Notice } from '../../../ui/fw';
import { post } from '../portalApi';

export function takeToken(win = window) {
  const m = /(?:^#|&)t=([^&]+)/.exec(win.location.hash || '');
  if (!m) return null;
  try { win.history.replaceState(null, '', `${win.location.pathname}${win.location.search}`); } catch (_) { /* best effort */ }
  try { return decodeURIComponent(m[1]); } catch (_) { return m[1]; }
}

export default function SignIn({ reason, loadError, signedIn, onSignedIn }) {
  const [token] = useState(() => takeToken());
  const [step, setStep] = useState(token ? 'link' : 'email');
  const [email, setEmail] = useState('');
  const [remember, setRemember] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sentNote, setSentNote] = useState(null);
  const codeRef = useRef(null);

  useEffect(() => { if (step === 'code' && codeRef.current) codeRef.current.focus(); }, [step]);

  if (signedIn && !token) return <Navigate to="/portal" replace />;

  const ask = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setError('Enter the email address the office has for you.'); return; }
    setBusy(true);
    try {
      const r = await post('/portal/auth/request', { email: email.trim(), remember });
      setSentNote((r && r.message) || 'If that address has an account, we have emailed it a sign-in link and a code.');
      setCode('');
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (body) => {
    setError(null);
    setBusy(true);
    try {
      await post('/portal/auth/verify', body);
      await onSignedIn();
    } catch (err) {
      setError(err.status === 429 ? err.message : (err.body && err.body.error) || err.message);
      setBusy(false);
    }
  };

  const useCode = (e) => {
    e.preventDefault();
    const clean = code.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(clean)) { setError('The code is six digits.'); return; }
    verify({ code: clean });
  };

  return (
    <div className="fw-app pt pt--signin" data-screen="portal-sign-in">
      <main className="pt-body pt-signin">
        <h1 className="pt-signin__title">Sign in to your account</h1>
        <p className="pt-muted">See and pay your invoices, and download statements, any time.</p>

        {reason ? <Notice tone="info" data-note="reason">{reason}</Notice> : null}
        {loadError ? <Notice tone="warn" data-note="load-error">{loadError}</Notice> : null}
        {error ? <Notice tone="stop" data-note="error">{error}</Notice> : null}

        {step === 'link' ? (
          <section className="pt-card" data-step="link">
            <p>You followed a sign-in link. Press Continue to open your account on this device.</p>
            <div className="pt-actions">
              <FwButton onClick={() => verify({ token })} loading={busy} data-action="continue">Continue</FwButton>
              <FwButton variant="ghost" onClick={() => { setError(null); setStep('email'); }} data-action="use-email">Email me a new link instead</FwButton>
            </div>
          </section>
        ) : null}

        {step === 'email' ? (
          <form className="pt-card" onSubmit={ask} noValidate data-step="email">
            <FwField label="Email address" htmlFor="pt-email" hint="The address the office sends your invoices to.">
              <FwInput id="pt-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FwField>
            <label className="pt-check">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-field="remember" />
              <span>Keep me signed in on this device for 30 days</span>
            </label>
            <div className="pt-actions">
              <FwButton type="submit" loading={busy} data-action="send-link">Email me a sign-in link</FwButton>
            </div>
          </form>
        ) : null}

        {step === 'code' ? (
          <form className="pt-card" onSubmit={useCode} noValidate data-step="code">
            <Notice tone="ok" data-note="sent">{sentNote}</Notice>
            <p>Open the email and press its button — or type the code from it here.</p>
            <FwField label="Code" htmlFor="pt-code">
              <FwInput id="pt-code" ref={codeRef} value={code} onChange={(e) => setCode(e.target.value)}
                inputMode="numeric" autoComplete="one-time-code" maxLength={9} className="pt-code" />
            </FwField>
            <div className="pt-actions">
              <FwButton type="submit" loading={busy} data-action="use-code">Sign in</FwButton>
              <FwButton variant="ghost" onClick={() => { setError(null); setStep('email'); }} data-action="start-over">Use a different email or send a new code</FwButton>
            </div>
          </form>
        ) : null}
      </main>
    </div>
  );
}
